'use client';

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { pushToDataLayer, initializeDataLayer } from '@udemy-v2/gtag';
import { useCoursePrice } from '@udemy-v2/react-course-pricing';
import { useClpCourse } from '@/features/course-landing-page/context/clp-course-provider.react-component';

/**
 * Determines whether dataLayer should be updated based on whether data has changed
 * Compares the actual data payload to detect any meaningful changes
 * @param lastDataJson - JSON string of last pushed data
 * @param currentDataJson - JSON string of current data
 * @returns true if data should be pushed, false if it's a duplicate
 */
function shouldPushToDataLayer(lastDataJson: string, currentDataJson: string): boolean {
  return lastDataJson !== currentDataJson;
}

/**
 * Course Landing Page GTM DataLayer Component
 *
 * This component manages the GTM dataLayer for course landing pages,
 * pushing course and pricing data when rendered within a ClpCourseProvider context.
 *
 * Race Condition Prevention:
 * - Ensures window.dataLayer is initialized before pushing data
 * - GTM's dataLayer works as a queue - data pushed before GTM loads is preserved
 * - When GTM loads, it processes all queued dataLayer items
 *
 * Duplicate Prevention:
 * - Tracks pushed data using refs to prevent duplicate pushes
 * - Only pushes when course ID or price status changes
 * - Prevents React re-renders from causing multiple identical dataLayer pushes
 *
 * DOM Integration:
 * - Adds data-clp-course-id attribute to <body> element for GTM variable access
 * - Automatically removes attribute on unmount
 *
 * It must be rendered INSIDE the ClpCourseProvider to have access to course data.
 */
export function ClpGtmDataLayer() {
  const $ = _c(26);
  const course = useClpCourse();
  const coursePrice = useCoursePrice(Number(course.id));
  const lastDataJsonRef = React.useRef("");
  let t0;
  if ($[0] !== course.categories?.[0]?.name || $[1] !== course.categories?.[0]?.subcategories?.[0]?.name || $[2] !== course.durationInSeconds || $[3] !== course.enrollments?.count || $[4] !== course.frontendTrackingId || $[5] !== course.id || $[6] !== course.instructors?.[0]?.id || $[7] !== course.instructors?.[0]?.name || $[8] !== course.isDirectLanded || $[9] !== course.isEnrolled || $[10] !== course.isFree || $[11] !== course.locale || $[12] !== course.primaryTopic?.name || $[13] !== course.rating?.average || $[14] !== course.rating?.count || $[15] !== course.title || $[16] !== course.topics?.[0]?.name || $[17] !== course.urlCourseLanding || $[18] !== coursePrice) {
    t0 = () => {
      initializeDataLayer();
      const courseData = {
        course_id: course.id,
        course_title: course.title,
        course_url: course.urlCourseLanding,
        course_topic: course.primaryTopic?.name || course.topics?.[0]?.name,
        course_category: course.categories?.[0]?.name,
        course_subcategory: course.categories?.[0]?.subcategories?.[0]?.name,
        course_language: course.locale?.split("_")[0] || "en",
        course_locale: course.locale || "en_US",
        course_length_minutes: course.durationInSeconds ? Math.round(course.durationInSeconds / 60) : undefined,
        course_num_enrollments: course.enrollments?.count,
        course_instructor_name: course.instructors?.[0]?.name,
        instructor_id: course.instructors?.[0]?.id,
        instructor_name: course.instructors?.[0]?.name,
        course_avg_rating: course.rating?.average,
        rating: course.rating?.average,
        num_reviews: course.rating?.count,
        is_paid: !course.isFree,
        is_free: course.isFree,
        is_enrolled: course.isEnrolled,
        frontend_tracking_id: course.frontendTrackingId,
        is_direct_landed: course.isDirectLanded,
        items: [{
          item_id: String(course.id),
          item_name: course.title,
          item_category: course.categories?.[0]?.name,
          item_category2: course.categories?.[0]?.subcategories?.[0]?.name,
          item_category3: course.primaryTopic?.name || course.topics?.[0]?.name
        }]
      };
      if (coursePrice?.status === "loaded") {
        courseData.list_price = coursePrice.listPrice?.amount;
        courseData.list_price_currency = coursePrice.listPrice?.currency;
        courseData.price = coursePrice.price?.amount;
        courseData.price_currency = coursePrice.price?.currency;
        courseData.has_discount = !!coursePrice.discountPrice;
        if (coursePrice.listPrice?.amount && coursePrice.discountPrice?.amount) {
          const discountPercent = Math.round((coursePrice.listPrice.amount - coursePrice.discountPrice.amount) / coursePrice.listPrice.amount * 100);
          courseData.discount_percent = discountPercent;
        }
      }
      const currentDataJson = JSON.stringify(courseData);
      if (!shouldPushToDataLayer(lastDataJsonRef.current, currentDataJson)) {
        return;
      }
      lastDataJsonRef.current = currentDataJson;
      pushToDataLayer(courseData);
      pushToDataLayer({
        event: "course-data-ready"
      });
    };
    $[0] = course.categories?.[0]?.name;
    $[1] = course.categories?.[0]?.subcategories?.[0]?.name;
    $[2] = course.durationInSeconds;
    $[3] = course.enrollments?.count;
    $[4] = course.frontendTrackingId;
    $[5] = course.id;
    $[6] = course.instructors?.[0]?.id;
    $[7] = course.instructors?.[0]?.name;
    $[8] = course.isDirectLanded;
    $[9] = course.isEnrolled;
    $[10] = course.isFree;
    $[11] = course.locale;
    $[12] = course.primaryTopic?.name;
    $[13] = course.rating?.average;
    $[14] = course.rating?.count;
    $[15] = course.title;
    $[16] = course.topics?.[0]?.name;
    $[17] = course.urlCourseLanding;
    $[18] = coursePrice;
    $[19] = t0;
  } else {
    t0 = $[19];
  }
  let t1;
  if ($[20] !== course || $[21] !== coursePrice) {
    t1 = [course, coursePrice];
    $[20] = course;
    $[21] = coursePrice;
    $[22] = t1;
  } else {
    t1 = $[22];
  }
  React.useEffect(t0, t1);
  let t2;
  let t3;
  if ($[23] !== course.id) {
    t2 = () => {
      document.body.setAttribute("data-clp-course-id", course.id);
      return _temp;
    };
    t3 = [course.id];
    $[23] = course.id;
    $[24] = t2;
    $[25] = t3;
  } else {
    t2 = $[24];
    t3 = $[25];
  }
  React.useEffect(t2, t3);
  return null;
}
function _temp() {
  document.body.removeAttribute("data-clp-course-id");
}